/*
 * <sku-counterpart>: shows a box linking a subscription product to its destockage counterpart
 * (and the other way round) when one exists. Both products share the same SKU.
 *
 * Rendered by the "sku-counterpart" block of sections/main-product.liquid. The element stays hidden
 * until a counterpart that is in stock is found, so nothing is displayed when there is none.
 *
 * Lookup: predictive search by SKU (candidates, filtered on the destockage tag), then
 * /products/<handle>?view=sku-counterpart (templates/product.sku-counterpart.liquid) to check the exact
 * SKU, the availability and get the price already formatted by Liquid.
 */
if (!customElements.get('sku-counterpart')) {
  const CACHE_PREFIX = 'sku-counterpart:'
  const CACHE_TTL = 10 * 60 * 1000
  const MAX_CANDIDATES = 3

  // Cached value can be null (no counterpart): undefined means "not cached"
  const readCache = (key) => {
    try {
      const raw = sessionStorage.getItem(key)
      if (!raw) return undefined
      const { t, v } = JSON.parse(raw)
      return Date.now() - t > CACHE_TTL ? undefined : v
    } catch (error) {
      return undefined
    }
  }

  const writeCache = (key, value) => {
    try {
      sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), v: value }))
    } catch (error) {
      // storage unavailable: lookups simply are not cached
    }
  }

  const normalize = (sku) => String(sku || '').trim().toLowerCase()

  class SkuCounterpart extends HTMLElement {
    connectedCallback() {
      // Type of the current page: "subscription" or "destock"
      this.pageType = this.dataset.pageType
      this.destockTag = normalize(this.dataset.destockTag || 'Destockage')
      this.currentSku = null
      this.requestId = 0

      this.productRoot = this.closest('.product')
      this.onVariantChange = (event) => this.refresh(event.detail && event.detail.variant && event.detail.variant.sku)
      if (this.productRoot) this.productRoot.addEventListener('on:variant:change', this.onVariantChange)

      this.refresh(this.dataset.sku)
    }

    disconnectedCallback() {
      if (this.productRoot) this.productRoot.removeEventListener('on:variant:change', this.onVariantChange)
    }

    async refresh(rawSku) {
      const sku = String(rawSku || '').trim()
      if (sku === this.currentSku) return
      this.currentSku = sku

      const requestId = ++this.requestId
      this.hidden = true
      if (!sku) return

      let counterpart = null
      try {
        counterpart = await this.findCounterpart(sku)
      } catch (error) {
        console.error('[sku-counterpart] lookup failed', error)
      }

      // A newer variant was selected while waiting
      if (requestId !== this.requestId) return
      if (counterpart) this.render(counterpart)
    }

    async findCounterpart(sku) {
      const key = `${CACHE_PREFIX}${this.pageType}:${normalize(sku)}`
      const cached = readCache(key)
      if (cached !== undefined) return cached

      const counterpart = await this.lookup(sku)
      writeCache(key, counterpart)
      return counterpart
    }

    isDestock(product) {
      return (product.tags || []).some((tag) => normalize(tag) === this.destockTag)
    }

    async lookup(sku) {
      const searchUrl = `${theme.routes.predictiveSearch}.json?q=${encodeURIComponent(sku)}`
        + '&resources[type]=product&resources[limit]=10&resources[options][fields]=variants.sku'
      const response = await fetch(searchUrl, { headers: { Accept: 'application/json' } })
      if (!response.ok) throw new Error(`search request failed (${response.status})`)

      const { resources } = await response.json()
      const products = (resources && resources.results && resources.results.products) || []

      // On a subscription page we look for a destockage product, and the other way round
      const wantDestock = this.pageType === 'subscription'
      const candidates = products
        .filter((product) => product.handle !== this.dataset.handle && product.available && this.isDestock(product) === wantDestock)
        .slice(0, MAX_CANDIDATES)

      for (const candidate of candidates) {
        const info = await this.fetchInfo(candidate)
        if (info && info.available && info.skus.some((candidateSku) => normalize(candidateSku) === normalize(sku))) return info
      }

      return null
    }

    async fetchInfo(candidate) {
      const url = new URL(candidate.url, window.location.origin)
      url.search = 'view=sku-counterpart'
      // No Accept: application/json header here, Shopify would then answer with its own product JSON
      // instead of the sku-counterpart view
      const response = await fetch(url.toString())
      if (!response.ok) return null
      return response.json()
    }

    render(info) {
      const { title, cta, prefix, suffix } = this.dataset

      const body = document.createElement('div')
      body.className = 'sku-counterpart__body'

      const heading = document.createElement('p')
      heading.className = 'sku-counterpart__title font-bold'
      heading.textContent = title
      body.append(heading)

      const price = document.createElement('p')
      price.className = 'sku-counterpart__price'
      if (prefix) price.append(`${prefix} `)
      const amount = document.createElement('strong')
      amount.textContent = info.price
      price.append(amount)
      if (suffix) price.append(suffix)
      if (info.compare_price) {
        const compare = document.createElement('s')
        compare.className = 'sku-counterpart__compare'
        compare.textContent = info.compare_price
        price.append(' ', compare)
      }
      body.append(price)

      const link = document.createElement('a')
      link.className = 'btn btn--secondary sku-counterpart__cta'
      link.href = info.url
      link.textContent = cta

      const card = document.createElement('div')
      card.className = 'sku-counterpart__card'
      card.append(body, link)

      this.replaceChildren(card)
      this.hidden = false
    }
  }

  customElements.define('sku-counterpart', SkuCounterpart)
}
